/**
 * sectionUtils.ts
 *
 * Utility functions for managing sections and their relationships with fields.
 * Provides functions for section-aware field operations while maintaining
 * backward compatibility with flat field structures.
 *
 * Key Principles:
 * - Fields array remains FLAT (critical for backward compatibility)
 * - Sections track which fields belong to them via fieldIds
 * - Fields track their section via sectionId
 * - All operations maintain consistency between fields and sections
 */

import { Field, Section } from "../types/types";

/**
 * Reassign orders to all elements on a page respecting section hierarchy
 * Section fields must have orders greater than their parent section
 * @param fields - All fields on the page
 * @param sections - All sections on the page
 * @returns Updated fields and sections with correct sequential orders
 */
export const reassignPageOrders = (
  fields: Field[],
  sections: Section[]
): { fields: Field[]; sections: Section[] } => {
  // Separate page-level fields from section fields
  const pageLevelFields = fields.filter(f => !f.sectionId);
  const sectionFields = fields.filter(f => f.sectionId);

  // Create elements array with page-level items only
  const pageLevelElements: Array<{ type: 'field' | 'section'; data: Field | Section }> = [
    ...pageLevelFields.map(f => ({ type: 'field' as const, data: f })),
    ...sections.map(s => ({ type: 'section' as const, data: s })),
  ];

  // Sort page-level elements by current order
  pageLevelElements.sort((a, b) => {
    const orderA = a.type === 'field' ? (a.data as Field).order ?? 0 : (a.data as Section).order;
    const orderB = b.type === 'field' ? (b.data as Field).order ?? 0 : (b.data as Section).order;
    return orderA - orderB;
  });

  // Reassign orders: sections get their order, then their fields, then continue
  let currentOrder = 0;
  const updatedFields: Field[] = [];
  const updatedSections: Section[] = [];

  pageLevelElements.forEach(element => {
    if (element.type === 'section') {
      const section = element.data as Section;
      // Assign order to section
      updatedSections.push({ ...section, order: currentOrder });
      currentOrder++;

      // Assign orders to fields in this section
      const fieldsInSection = sectionFields
        .filter(f => f.sectionId === section.id)
        .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

      fieldsInSection.forEach(field => {
        updatedFields.push({ ...field, order: currentOrder });
        currentOrder++;
      });
    } else {
      // Page-level field
      const field = element.data as Field;
      updatedFields.push({ ...field, order: currentOrder });
      currentOrder++;
    }
  });

  return { fields: updatedFields, sections: updatedSections };
};

/**
 * Get all fields that belong to a specific section
 * @param fields - Flat array of all fields
 * @param sectionId - ID of the section
 * @returns Array of fields belonging to the section
 */
export const getFieldsBySection = (
  fields: Field[],
  sectionId: string
): Field[] => {
  return fields.filter((field) => field.sectionId === sectionId);
};

/**
 * Get all fields that don't belong to any section on a specific page
 * @param fields - Flat array of all fields
 * @param pageId - ID of the page
 * @returns Array of fields not in any section
 */
export const getUnsectionedFields = (
  fields: Field[],
  pageId: string
): Field[] => {
  return fields.filter(
    (field) => field.pageId === pageId && !field.sectionId
  );
};

/**
 * Get all sections for a specific page
 * @param sections - Array of all sections
 * @param pageId - ID of the page
 * @returns Array of sections on the page, sorted by order
 */
export const getSectionsByPage = (
  sections: Section[],
  pageId: string
): Section[] => {
  return sections
    .filter((section) => section.pageId === pageId)
    .sort((a, b) => a.order - b.order);
};

/**
 * Get all page elements (fields and sections) in render order
 * This is used for drag-and-drop where sections and fields are siblings
 * IMPORTANT: Only includes PAGE-LEVEL fields (without sectionId)
 * Section fields are rendered INSIDE their sections, not as page elements
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param pageId - ID of the page
 * @returns Array of mixed elements with type information
 */
export const getPageElements = (
  fields: Field[],
  sections: Section[],
  pageId: string
): Array<{ type: "field" | "section"; data: Field | Section; order: number }> => {
  // Only include page-level fields (those WITHOUT a sectionId)
  // Fields with sectionId are rendered inside their sections, not as page elements
  const pageLevelFields = fields.filter(
    (f) => f.pageId === pageId && !f.sectionId
  );
  const pageSections = sections.filter((s) => s.pageId === pageId);

  const elements = [
    ...pageLevelFields.map((field) => ({
      type: "field" as const,
      data: field,
      order: field.order ?? 0,
    })),
    ...pageSections.map((section) => ({
      type: "section" as const,
      data: section,
      order: section.order,
    })),
  ];

  return elements.sort((a, b) => a.order - b.order);
};

/**
 * Move a field to a section or remove it from a section
 * @param field - Field to move
 * @param targetSectionId - ID of target section (null/undefined to remove from section)
 * @returns Updated field with new sectionId
 */
export const moveFieldToSection = (
  field: Field,
  targetSectionId: string | null | undefined
): Field => {
  return {
    ...field,
    sectionId: targetSectionId || undefined,
  };
};

/**
 * Update section's fieldIds array based on current fields
 * Ensures consistency between fields' sectionId and section's fieldIds
 * @param section - Section to update
 * @param fields - Flat array of all fields
 * @returns Updated section with correct fieldIds
 */
export const updateSectionFieldIds = (
  section: Section,
  fields: Field[]
): Section => {
  const fieldIds = fields
    .filter((f) => f.sectionId === section.id)
    .map((f) => f.id);

  return {
    ...section,
    fieldIds,
  };
};

/**
 * Reorder a field within its section
 * Used when dragging a field that has a sectionId
 * NOTE: sourceIndex and destinationIndex are GLOBAL indices, not relative to the section
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param pageId - ID of the current page
 * @param sectionId - ID of the section containing the field
 * @param fieldId - ID of the field being reordered
 * @param globalSourceIndex - Original index in the full page (GLOBAL)
 * @param globalDestinationIndex - New index in the full page (GLOBAL)
 * @returns Updated fields and sections arrays
 */
export const reorderFieldWithinSection = (
  fields: Field[],
  sections: Section[],
  pageId: string,
  sectionId: string,
  fieldId: string,
  globalSourceIndex: number,
  globalDestinationIndex: number
): { fields: Field[]; sections: Section[] } => {
  // Get all fields in this section, sorted by order
  const sectionFields = fields
    .filter((f) => f.sectionId === sectionId)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  // Find all GLOBAL indices that belong to this section
  const sectionGlobalIndices = fields.reduce<number[]>((acc, field, index) => {
    if (field.sectionId === sectionId) {
      acc.push(index);
    }
    return acc;
  }, []);

  // Verify both source and destination are within this section's fields
  const sourceInSection = sectionGlobalIndices.includes(globalSourceIndex);
  const destInSection = sectionGlobalIndices.includes(globalDestinationIndex);

  if (!sourceInSection || !destInSection) {
    // One of the indices is outside the section - field is moving out
    // Return unchanged to prevent the field from disappearing
    return { fields, sections };
  }

  // Find the field being moved by ID
  const fieldIndex = sectionFields.findIndex((f) => f.id === fieldId);
  if (fieldIndex === -1) return { fields, sections }; // Field not found

  // Convert global destination index to relative index within section
  // sectionGlobalIndices uses physical array position; sectionFields is sorted by .order
  // so we must look up the target field by ID in sectionFields to get the correct relative index
  const targetField = fields[globalDestinationIndex];
  const relativeDestinationIndex = sectionFields.findIndex(f => f.id === targetField?.id);
  if (relativeDestinationIndex === -1) return { fields, sections };

  // Reorder within the section using relative indices
  const reorderedFields = [...sectionFields];
  const [movedField] = reorderedFields.splice(fieldIndex, 1);
  reorderedFields.splice(relativeDestinationIndex, 0, movedField);

  // Get other fields (not in this section) on this page
  const otherFieldsOnPage = fields.filter((f) => f.pageId === pageId && f.sectionId !== sectionId);
  const fieldsOnOtherPages = fields.filter((f) => f.pageId !== pageId);

  // Get sections on this page
  const sectionsOnPage = sections.filter((s) => s.pageId === pageId);
  const sectionsOnOtherPages = sections.filter((s) => s.pageId !== pageId);

  // Combine all fields on this page
  const allFieldsOnPage = [...otherFieldsOnPage, ...reorderedFields];

  // IMPORTANT: Reassign orders respecting section hierarchy
  // Section fields must have orders greater than their parent section
  const { fields: finalFieldsOnPage, sections: finalSectionsOnPage } = reassignPageOrders(
    allFieldsOnPage,
    sectionsOnPage
  );

  return {
    fields: [...fieldsOnOtherPages, ...finalFieldsOnPage],
    sections: [...sectionsOnOtherPages, ...finalSectionsOnPage],
  };
};

/**
 * Reorder page elements (fields and sections) after drag-and-drop
 * Maintains flat field array while respecting section grouping
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param pageId - ID of the page
 * @param sourceIndex - Original index in combined elements array
 * @param destinationIndex - New index in combined elements array
 * @returns Object with updated fields and sections arrays
 */
export const reorderPageElements = (
  fields: Field[],
  sections: Section[],
  pageId: string,
  sourceIndex: number,
  destinationIndex: number
): { fields: Field[]; sections: Section[] } => {
  // Get all page elements in order
  const elements = getPageElements(fields, sections, pageId);

  // Extract the moved element
  const [movedElement] = elements.splice(sourceIndex, 1);

  // Insert at new position
  elements.splice(destinationIndex, 0, movedElement);

  // Reassign order values - preserve existing sectionId
  // Note: sectionId should ONLY change via explicit drop zone or handleFieldUpdate,
  // NOT via reordering position
  elements.forEach((element, index) => {
    if (element.type === "section") {
      // Update section order immutably
      if (element.data.order !== index) {
        element.data = {
          ...element.data,
          order: index,
        };
      }
    } else if (element.type === "field") {
      const field = element.data as Field;
      // Only update order, preserve existing sectionId
      if (field.order !== index) {
        element.data = {
          ...field,
          order: index,
          // sectionId intentionally preserved from original field
        };
      }
    }
  });

  // Separate back into fields and sections
  const updatedPageFields = elements
    .filter((e) => e.type === "field")
    .map((e) => e.data as Field);

  const updatedPageSections = elements
    .filter((e) => e.type === "section")
    .map((e) => e.data as Section);

  // Update each section's fieldIds to match the fields that now belong to it
  const finalPageSections = updatedPageSections.map((section) =>
    updateSectionFieldIds(section, updatedPageFields)
  );

  // Merge with other pages' fields/sections
  const otherPageFields = fields.filter((f) => f.pageId !== pageId);
  const otherPageSections = sections.filter((s) => s.pageId !== pageId);

  // Get section fields on this page
  const sectionFieldsOnPage = fields.filter((f) => f.pageId === pageId && f.sectionId);

  // Combine all fields and sections on this page
  const allFieldsOnPage = [...updatedPageFields, ...sectionFieldsOnPage];

  // IMPORTANT: Reassign orders respecting section hierarchy
  // Section fields must have orders greater than their parent section
  const { fields: reorderedFields, sections: reorderedSections } = reassignPageOrders(
    allFieldsOnPage,
    finalPageSections
  );

  return {
    fields: [...otherPageFields, ...reorderedFields],
    sections: [...otherPageSections, ...reorderedSections],
  };
};

/**
 * Reorder a section along with all its fields
 * When a section is moved, all fields inside it should maintain their relative positions
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param pageId - ID of the page
 * @param sourceIndex - Original index in combined elements array
 * @param destinationIndex - New index in combined elements array
 * @param draggedItemId - The draggableId that was dragged
 * @returns Object with updated fields and sections arrays
 */
export const reorderSectionWithFields = (
  fields: Field[],
  sections: Section[],
  pageId: string,
  sourceIndex: number,
  destinationIndex: number,
  draggedItemId: string
): { fields: Field[]; sections: Section[] } => {
  // Check if a section is being dragged
  const isSectionDrag = draggedItemId.startsWith("section-");

  if (!isSectionDrag) {
    // Regular field drag - use existing logic
    return reorderPageElements(fields, sections, pageId, sourceIndex, destinationIndex);
  }

  // Extract section ID from draggableId
  const sectionId = draggedItemId.replace("section-", "");

  // Get all page elements
  const elements = getPageElements(fields, sections, pageId);

  // Extract just the section (NOT its fields - they stay where they are via sectionId)
  const elementsToMove: typeof elements = [];
  const remainingElements: typeof elements = [];

  elements.forEach((element) => {
    if (element.type === "section" && element.data.id === sectionId) {
      // This is the dragged section
      elementsToMove.push(element);
    } else {
      // All other elements (page-level fields and other sections)
      remainingElements.push(element);
    }
  });

  // Calculate the adjusted destination index for the remainingElements array
  // After removing moved elements, we need to adjust the destination index
  //
  // Example: [Section(0), FieldA(1), Input1(2), Input2(3)]
  // Drag Section+FieldA from 0-1 to position 2
  //
  // After extraction: remainingElements = [Input1, Input2]
  // We want to insert at position 2 in the FINAL array: [Input1, Input2, Section, FieldA]
  // In remainingElements, position 2 means after Input2, so splice(2, 0, ...)
  //
  // BUT: splice uses indices of the current array! remainingElements only has 2 items (indices 0, 1)
  // So we need: splice(2, 0, ...) which appends at end ✓
  //
  // General rule matching standard reorderPageElements behavior:
  // The destinationIndex is meant for the FINAL array (after all moves).
  // We just use it directly with splice, which handles out-of-bounds gracefully.

  remainingElements.splice(destinationIndex, 0, ...elementsToMove);

  // Reassign order values for page-level elements
  // CRITICAL: Section fields keep their sectionId and are NOT reordered here
  remainingElements.forEach((element, index) => {
    if (element.type === "section") {
      // Update section order immutably
      element.data = {
        ...element.data,
        order: index,
      };
    } else if (element.type === "field") {
      const field = element.data as Field;
      // Only update page-level field order
      element.data = {
        ...field,
        order: index,
      };
    }
  });

  // Separate back into elements
  const updatedPageLevelFields = remainingElements
    .filter((e) => e.type === "field")
    .map((e) => e.data as Field);

  const updatedPageSections = remainingElements
    .filter((e) => e.type === "section")
    .map((e) => e.data as Section);

  // Get section fields on this page (they kept their old orders)
  const allPageFields = fields.filter((f) => f.pageId === pageId);
  const allSectionFields = allPageFields.filter((f) => f.sectionId);

  // Update each section's fieldIds to match the fields that now belong to it
  const tempPageSections = updatedPageSections.map((section) =>
    updateSectionFieldIds(section, [...updatedPageLevelFields, ...allSectionFields])
  );

  // Combine all fields on this page
  const allFieldsOnPage = [...updatedPageLevelFields, ...allSectionFields];

  // IMPORTANT: Reassign orders respecting section hierarchy
  // Section fields must have orders greater than their parent section
  const { fields: finalPageFields, sections: finalPageSections } = reassignPageOrders(
    allFieldsOnPage,
    tempPageSections
  );

  // Merge with other pages' fields/sections
  const updatedFields = [
    ...fields.filter((f) => f.pageId !== pageId),
    ...finalPageFields,
  ];

  const updatedSections = [
    ...sections.filter((s) => s.pageId !== pageId),
    ...finalPageSections,
  ];

  return {
    fields: updatedFields,
    sections: updatedSections,
  };
};

/**
 * Move a field between sections within the same page
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param fieldId - ID of field to move
 * @param targetSectionId - ID of target section (null to remove from section)
 * @returns Object with updated fields and sections arrays
 */
export const moveFieldBetweenSections = (
  fields: Field[],
  sections: Section[],
  fieldId: string,
  targetSectionId: string | null
): { fields: Field[]; sections: Section[] } => {
  // Update the field's sectionId
  const updatedFields = fields.map((field) =>
    field.id === fieldId
      ? moveFieldToSection(field, targetSectionId)
      : field
  );

  // Update all affected sections' fieldIds
  const updatedSections = sections.map((section) =>
    updateSectionFieldIds(section, updatedFields)
  );

  return {
    fields: updatedFields,
    sections: updatedSections,
  };
};

/**
 * Move a field from a section to page level and position it
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @param pageId - ID of the page
 * @param fieldId - ID of field to move
 * @param targetOrder - Desired order at page level
 * @returns Object with updated fields and sections arrays
 */
export const moveFieldToPageLevelWithPosition = (
  fields: Field[],
  sections: Section[],
  pageId: string,
  fieldId: string,
  targetOrder: number
): { fields: Field[]; sections: Section[] } => {
  // Find the field being moved
  const field = fields.find((f) => f.id === fieldId);
  if (!field) return { fields, sections };

  // Remove field from section and set target order
  const fieldWithoutSection = fields.map((f) =>
    f.id === fieldId
      ? { ...f, sectionId: undefined, order: targetOrder }
      : f
  );

  // Get all fields and sections on this page
  const fieldsOnPage = fieldWithoutSection.filter((f) => f.pageId === pageId);
  const fieldsOnOtherPages = fieldWithoutSection.filter((f) => f.pageId !== pageId);
  const sectionsOnPage = sections.filter((s) => s.pageId === pageId);
  const sectionsOnOtherPages = sections.filter((s) => s.pageId !== pageId);

  // Update all affected sections' fieldIds
  const tempSectionsOnPage = sectionsOnPage.map((section) =>
    updateSectionFieldIds(section, fieldsOnPage)
  );

  // IMPORTANT: Reassign orders respecting section hierarchy
  // Section fields must have orders greater than their parent section
  const { fields: finalFieldsOnPage, sections: finalSectionsOnPage } = reassignPageOrders(
    fieldsOnPage,
    tempSectionsOnPage
  );

  return {
    fields: [...fieldsOnOtherPages, ...finalFieldsOnPage],
    sections: [...sectionsOnOtherPages, ...finalSectionsOnPage],
  };
};

/**
 * Delete a section and optionally move its fields out
 * @param sections - Array of all sections
 * @param fields - Flat array of all fields
 * @param sectionId - ID of section to delete
 * @param moveFieldsToSection - ID of section to move fields to (null to unsection them)
 * @returns Object with updated fields and sections arrays
 */
export const deleteSection = (
  sections: Section[],
  fields: Field[],
  sectionId: string,
  moveFieldsToSection: string | null = null
): { fields: Field[]; sections: Section[] } => {
  // Find the page of the deleted section
  const deletedSection = sections.find((s) => s.id === sectionId);
  const pageId = deletedSection?.pageId;

  // Remove the section
  const remainingSections = sections.filter((s) => s.id !== sectionId);

  // Update fields that belonged to the deleted section
  const updatedFields = fields.map((field) =>
    field.sectionId === sectionId
      ? moveFieldToSection(field, moveFieldsToSection)
      : field
  );

  // IMPORTANT: Reassign orders to ALL elements on the affected page to avoid gaps/duplicates
  if (pageId) {
    const fieldsOnPage = updatedFields.filter((f) => f.pageId === pageId);
    const fieldsOnOtherPages = updatedFields.filter((f) => f.pageId !== pageId);
    const sectionsOnPage = remainingSections.filter((s) => s.pageId === pageId);
    const sectionsOnOtherPages = remainingSections.filter((s) => s.pageId !== pageId);

    // IMPORTANT: Reassign orders respecting section hierarchy
    // Section fields must have orders greater than their parent section
    const { fields: finalFieldsOnPage, sections: finalSectionsOnPage } = reassignPageOrders(
      fieldsOnPage,
      sectionsOnPage
    );

    return {
      fields: [...fieldsOnOtherPages, ...finalFieldsOnPage],
      sections: [...sectionsOnOtherPages, ...finalSectionsOnPage],
    };
  }

  return {
    fields: updatedFields,
    sections: remainingSections,
  };
};

/**
 * Create a new section
 * @param pageId - ID of the page
 * @param title - Title of the section
 * @param existingSections - Array of existing sections on the page
 * @param existingFields - Array of existing fields on the page (optional for backward compatibility)
 * @returns New section object
 */
export const createSection = (
  pageId: string,
  title: string,
  existingSections: Section[],
  existingFields?: Field[]
): Section => {
  // IMPORTANT: Must consider BOTH fields and sections to avoid duplicate orders
  // If existingFields not provided, fall back to old logic (sections only)
  let order: number;

  if (existingFields) {
    // Count all fields and sections on this page to get next available order
    const pageFields = existingFields.filter(f => f.pageId === pageId);
    const pageSections = existingSections.filter(s => s.pageId === pageId);
    order = pageFields.length + pageSections.length;
  } else {
    // Fallback: old logic (only considers sections)
    const pageSections = getSectionsByPage(existingSections, pageId);
    const maxOrder = pageSections.length > 0
      ? Math.max(...pageSections.map((s) => s.order))
      : -1;
    order = maxOrder + 1;
  }

  return {
    id: `section-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`,
    title,
    pageId,
    order,
    collapsible: true,
    collapsed: false,
    fieldIds: [],
  };
};

/**
 * Count fields in a section
 * @param fields - Flat array of all fields
 * @param sectionId - ID of the section
 * @returns Number of fields in the section
 */
export const countFieldsInSection = (
  fields: Field[],
  sectionId: string
): number => {
  return fields.filter((f) => f.sectionId === sectionId).length;
};

/**
 * Check if a section is empty
 * @param fields - Flat array of all fields
 * @param sectionId - ID of the section
 * @returns True if section has no fields
 */
export const isSectionEmpty = (
  fields: Field[],
  sectionId: string
): boolean => {
  return countFieldsInSection(fields, sectionId) === 0;
};

/**
 * Validate section-field consistency
 * Ensures that field.sectionId matches section.fieldIds
 * @param fields - Flat array of all fields
 * @param sections - Array of all sections
 * @returns Object with validation status and any inconsistencies found
 */
export const validateSectionConsistency = (
  fields: Field[],
  sections: Section[]
): {
  isValid: boolean;
  errors: Array<{ type: string; message: string; fieldId?: string; sectionId?: string }>;
} => {
  const errors: Array<{ type: string; message: string; fieldId?: string; sectionId?: string }> = [];

  // Check 1: Every field with sectionId should reference an existing section
  fields.forEach((field) => {
    if (field.sectionId) {
      const section = sections.find((s) => s.id === field.sectionId);
      if (!section) {
        errors.push({
          type: "missing_section",
          message: `Field "${field.label}" (${field.id}) references non-existent section ${field.sectionId}`,
          fieldId: field.id,
          sectionId: field.sectionId,
        });
      }
    }
  });

  // Check 2: Every section's fieldIds should match actual fields with that sectionId
  sections.forEach((section) => {
    const actualFieldIds = fields
      .filter((f) => f.sectionId === section.id)
      .map((f) => f.id)
      .sort();

    const declaredFieldIds = (section.fieldIds || []).sort();

    if (JSON.stringify(actualFieldIds) !== JSON.stringify(declaredFieldIds)) {
      errors.push({
        type: "fieldIds_mismatch",
        message: `Section "${section.title}" (${section.id}) has inconsistent fieldIds`,
        sectionId: section.id,
      });
    }
  });

  return {
    isValid: errors.length === 0,
    errors,
  };
};
